/* eslint-disable no-restricted-imports */
import React, { ReactElement, useCallback, useMemo } from 'react';
import { useOpenDialog } from '../../../routing/ModalRoute';
import { DIALOGS } from '../../../routing/constants';
import { getIsMobile } from '../../../utils/getIsMobile';
import { useFavoritesEnabled } from '../../../utils/useFavoritesEnabled';
import { useTranslation } from '../../../utils/useTranslation';
import { IconShare } from '../../icon/IconShare';
import { IconShoppingBagAdd } from '../../icon/IconShoppingBagAdd';
import { Button } from '../../input/Button';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Step } from '../../users/PromoteShowInFeedDrawer/types';
import { CollectionStatus, CollectionUI } from '../../users/types';
import {
    BookmarkFilledIcon,
    BookmarkIcon,
    Container,
    ContentContainer,
    FavoriteHeartIcon,
    LikeThumbFilledIcon,
    LikeThumbIcon,
    TextFreeButton,
} from './styles';

const getButtons = (
    collection: CollectionUI,
    onShare?: VoidFunction,
    onReaction?: VoidFunction,
    onShowAddPostDialog?: VoidFunction,
    onRemoveSaved?: VoidFunction,
    onSave?: VoidFunction,
) => {
    const t = useTranslation();
    const { isMobile } = getIsMobile();

    const openDialog = useOpenDialog();

    const promoteInFeedEnabled = collection.promoteShowInFeedAvailable;
    const isFavoritesEnabled = useFavoritesEnabled();

    const openCreateCollection = useCallback(() => {
        openDialog(DIALOGS.CREATE_COLLECTION);
    }, [openDialog]);

    const onPromoteInFeed = useCallback(() => {
        openDialog(DIALOGS.PROMOTE_SHOW_IN_FEED_COLLECTION_DIALOG_NAME, {
            collectionId: collection.id,
            step: Step.CHOOSE_NAME,
        });
    }, [collection.id, openDialog]);

    const isPublic = collection.status === CollectionStatus.PUBLISHED && collection.isPublic;

    const shareButton = (
        <Button
            size="l"
            color="secondary"
            width={promoteInFeedEnabled ? 48 : '100%'}
            spacing={1}
            onClick={onShare}
        >
            <IconShare size={24} />
            {!promoteInFeedEnabled && t('Поделиться')}
        </Button>
    );

    const addPostButton = (
        <Button
            size="l"
            color="secondary"
            minWidth={promoteInFeedEnabled ? undefined : 175}
            width={promoteInFeedEnabled ? 48 : '100%'}
            spacing={1}
            onClick={onShowAddPostDialog}
            dataTestId={DataTestId.ADD_POST_BUTTON}
        >
            <IconShoppingBagAdd size={24} />
            {!promoteInFeedEnabled && t('Добавить товар')}
        </Button>
    );

    const createCollectionButton = (
        <Button
            width="100%"
            minWidth={170}
            size="l"
            spacing={1}
            onClick={openCreateCollection}
            dataTestId={DataTestId.COLLECTION_CREATE_FROM_FOOTER}
        >
            {t('Создать коллекцию')}
        </Button>
    );

    const promoteInFeedButton = (
        <Button
            size="l"
            minWidth={155}
            width="100%"
            spacing={1}
            onClick={onPromoteInFeed}
            dataTestId={DataTestId.COLLECTION_ADD_TO_FEED}
        >
            {t('Добавить в ленту')}
        </Button>
    );

    const onBookmark = useCallback(() => {
        if (collection.isSaved) {
            onRemoveSaved?.();
        } else {
            onSave?.();
        }
    }, [collection.isSaved, onSave, onRemoveSaved]);

    const { likeIcon, bookmarkIcon } = useMemo(() => {
        let like: ReactElement;
        if (isFavoritesEnabled) {
            like = collection.isLiked ? <LikeThumbFilledIcon /> : <LikeThumbIcon />;
        } else {
            like = <FavoriteHeartIcon isActive={collection.isLiked} />;
        }

        let bookmark: ReactElement;
        if (isFavoritesEnabled) {
            bookmark = <FavoriteHeartIcon isActive={collection.isSaved} />;
        } else {
            bookmark = collection.isSaved ? <BookmarkFilledIcon /> : <BookmarkIcon />;
        }

        return { likeIcon: like, bookmarkIcon: bookmark };
    }, [isFavoritesEnabled, collection.isLiked, collection.isSaved]);

    const bookmarkButton = (
        <Button
            color="secondary"
            size="l"
            width="48px"
            spacing={1}
            onClick={onBookmark}
            dataTestId={DataTestId.STICKY_FOOTER_ACTIONS_BOOKMARK}
        >
            {bookmarkIcon}
        </Button>
    );

    if (collection.modifiable) {
        return (
            <ContentContainer>
                {isPublic ? shareButton : null}
                {addPostButton}
                {promoteInFeedEnabled ? promoteInFeedButton : null}
            </ContentContainer>
        );
    }

    if (isMobile) {
        return (
            <ContentContainer>
                <TextFreeButton
                    onClick={onReaction}
                    dataTestId={DataTestId.STICKY_FOOTER_ACTIONS_LIKE}
                >
                    {likeIcon}
                </TextFreeButton>
                {bookmarkButton}
                <TextFreeButton onClick={onShare} mr={0.5}>
                    <IconShare size={24} fill="var(--color-primary)" />
                </TextFreeButton>
                {createCollectionButton}
            </ContentContainer>
        );
    }

    return (
        <ContentContainer>
            <Button
                color="secondary"
                size="l"
                width={48}
                spacing={1}
                onClick={onReaction}
                dataTestId={DataTestId.STICKY_FOOTER_ACTIONS_LIKE}
            >
                {likeIcon}
            </Button>
            {bookmarkButton}
            <Button
                color="secondary"
                size="l"
                width={48}
                spacing={1}
                onClick={onShare}
                dataTestId={DataTestId.SHARE_BUTTON}
            >
                <IconShare size={24} />
            </Button>
            {createCollectionButton}
        </ContentContainer>
    );
};

interface StickyFooterProps {
    collection: CollectionUI;
    onShare?: VoidFunction;
    onReaction?: VoidFunction;
    onShowAddPostDialog?: VoidFunction;
    onRemoveSaved?: VoidFunction;
    onSave?: VoidFunction;
}

export const StickyFooter = (props: StickyFooterProps) => {
    const { collection, onShare, onReaction, onShowAddPostDialog, onRemoveSaved, onSave } = props;

    return (
        <Container>
            {getButtons(
                collection,
                onShare,
                onReaction,
                onShowAddPostDialog,
                onRemoveSaved,
                onSave,
            )}
        </Container>
    );
};
